import type { Me } from "@cloudserver/contracts";
import { KeyRound, ShieldCheck, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddPasskey,
  usePasskeys,
  useRemovePasskey,
  useTotpDisable,
  useTotpEnable,
  useTotpSetup,
} from "../api/queries.js";
import { ErrorMessage } from "../ui/feedback.js";
import { dateTime } from "../ui/format.js";
import { DangerDialog } from "../ui/confirm.js";
import { PageHeader } from "../ui/layout.js";
import { Alert, Badge, Button, Card, Spinner, TextField } from "../ui/primitives.js";

function Passkeys() {
  const { t, i18n } = useTranslation();
  const list = usePasskeys();
  const add = useAddPasskey();
  const remove = useRemovePasskey();
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get("name") ?? "").trim();
    add.mutate(name || t("security.passkeyNameDefault"));
  };
  return (
    <Card className="flex flex-col gap-4">
      <header className="flex items-start gap-3">
        <KeyRound className="mt-0.5 size-5 text-accent" aria-hidden />
        <div>
          <h2 className="font-semibold">{t("security.passkeys")}</h2>
          <p className="text-sm text-muted">{t("security.passkeysLead")}</p>
        </div>
      </header>
      {list.isPending ? (
        <Spinner label={t("common.loading")} />
      ) : list.data?.length ? (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {list.data.map((key) => (
            <li
              key={key.id}
              className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
            >
              <div>
                <p className="text-sm font-medium">{key.name}</p>
                <p className="text-xs text-muted">
                  {key.lastUsedAt
                    ? t("security.lastUsed", { date: dateTime(key.lastUsedAt, i18n.language) })
                    : t("security.neverUsed")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge>{key.backedUp ? t("security.synced") : t("security.deviceBound")}</Badge>
                <Button
                  variant="danger"
                  onClick={() => setRemoving({ id: key.id, name: key.name })}
                >
                  {t("common.remove")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">{t("security.noPasskeys")}</p>
      )}
      <ErrorMessage error={remove.error} />
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <TextField
          className="min-w-48 flex-1"
          label={t("security.passkeyName")}
          name="name"
          placeholder={t("security.passkeyNameDefault")}
          maxLength={64}
        />
        <Button type="submit" busy={add.isPending}>
          {t("security.addPasskey")}
        </Button>
      </form>
      <ErrorMessage error={add.error} />
      <DangerDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={t("security.removePasskeyTitle", { name: removing?.name ?? "" })}
        consequences={[t("security.removePasskeyEffect"), t("security.removePasskeyKeep")]}
        actionLabel={t("common.remove")}
        busy={remove.isPending}
        onConfirm={() =>
          removing && remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })
        }
      />
    </Card>
  );
}

function Totp({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const setup = useTotpSetup();
  const enable = useTotpEnable();
  const disable = useTotpDisable();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [disabling, setDisabling] = useState(false);

  const confirm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const code = String(new FormData(event.currentTarget).get("code") ?? "").trim();
    enable.mutate(code, { onSuccess: (result) => setCodes(result.codes) });
  };

  return (
    <Card className="flex flex-col gap-4">
      <header className="flex items-start gap-3">
        <Smartphone className="mt-0.5 size-5 text-accent" aria-hidden />
        <div className="flex-1">
          <h2 className="flex items-center gap-2 font-semibold">
            {t("security.totp")}
            {enabled && <Badge tone="ok">{t("security.totpEnabled")}</Badge>}
          </h2>
          <p className="text-sm text-muted">{t("security.totpLead")}</p>
        </div>
      </header>

      {codes ? (
        <div className="flex flex-col gap-3">
          <Alert tone="warn">
            <p className="font-medium">{t("security.recoveryTitle")}</p>
            <p>{t("security.recoveryLead")}</p>
          </Alert>
          <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
            {codes.map((code) => (
              <li key={code} className="rounded-md bg-surface-2 px-3 py-2">
                {code}
              </li>
            ))}
          </ul>
          <Button variant="secondary" onClick={() => setCodes(null)}>
            {t("security.recoveryDone")}
          </Button>
        </div>
      ) : enabled ? (
        <div>
          <Button variant="danger" onClick={() => setDisabling(true)}>
            {t("security.totpDisable")}
          </Button>
          <ErrorMessage error={disable.error} />
          <DangerDialog
            open={disabling}
            onOpenChange={setDisabling}
            title={t("security.totpDisableTitle")}
            consequences={[t("security.totpDisableEffect"), t("security.totpDisableCodes")]}
            actionLabel={t("security.totpDisable")}
            busy={disable.isPending}
            onConfirm={() => disable.mutate(undefined, { onSuccess: () => setDisabling(false) })}
          />
        </div>
      ) : setup.data ? (
        <form onSubmit={confirm} className="flex flex-col gap-4">
          <p className="text-sm">{t("security.totpScan")}</p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="rounded-lg bg-white p-3">
              <QRCodeSVG value={setup.data.uri} size={160} />
            </div>
            <div className="min-w-0 text-sm">
              <p className="text-muted">{t("security.totpKey")}</p>
              <p className="break-all font-mono">{setup.data.secret}</p>
            </div>
          </div>
          <TextField
            label={t("signIn.code")}
            name="code"
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]{6,7}"
          />
          <ErrorMessage error={enable.error} />
          <Button type="submit" busy={enable.isPending}>
            {t("security.totpConfirm")}
          </Button>
        </form>
      ) : (
        <div>
          <Button variant="secondary" onClick={() => setup.mutate()} busy={setup.isPending}>
            {t("security.totpSetup")}
          </Button>
          <ErrorMessage error={setup.error} />
        </div>
      )}
    </Card>
  );
}

export function SecurityPage({ me }: { me: Me }) {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t("security.title")} intro={t("security.intro")} help="security" />
      {me.secondFactorRequired && (
        <Alert tone="warn">
          <span className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 size-4 shrink-0" aria-hidden />
            {t("security.required")}
          </span>
        </Alert>
      )}
      <Passkeys />
      <Totp enabled={me.totpEnabled} />
    </>
  );
}
